import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { useLanguage } from '../hooks/useLanguage';

const GoogleCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [hasProcessed, setHasProcessed] = useState(false);

  const code = searchParams.get('code');
  const error = searchParams.get('error');

  useEffect(() => {
    // Prevent multiple calls if already processed
    if (hasProcessed) {
      return;
    }

    if (error) {
      setMessage({
        type: 'error',
        text: 'Google authentication was cancelled or failed'
      });
      setIsLoading(false);
      setHasProcessed(true);
      return;
    }

    if (!code) {
      setMessage({
        type: 'error',
        text: 'No authorization code received from Google'
      });
      setIsLoading(false);
      setHasProcessed(true);
      return;
    }

    const handleGoogleCallback = async () => {
      try {
        const result = await authService.handleGoogleCallback(code);
        
        if (result.success && result.user && result.token) {
          // Set the user in the auth context
          setUser(result.user);
          
          // Dispatch custom event to notify other components about auth state change
          window.dispatchEvent(new CustomEvent('customAuthStateChange'));
          
          setMessage({
            type: 'success',
            text: 'Google authentication successful!'
          });

          // Redirect to homepage after successful login
          setTimeout(() => {
            navigate('/');
          }, 1500);
        } else {
          setMessage({
            type: 'error',
            text: 'Google authentication failed'
          });
        }
      } catch (error: any) {
        console.error('Google callback failed:', error);
        setMessage({
          type: 'error',
          text: error.message || 'Google authentication failed'
        });
      } finally {
        setIsLoading(false);
        setHasProcessed(true);
      }
    };

    handleGoogleCallback();
  }, [code, error, navigate, setUser, hasProcessed]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Google Authentication
            </h1>
            <p className="text-gray-600 text-sm">
              Processing your Google authentication...
            </p>
          </div>

          {isLoading && (
            <div className="text-center">
              <div className="inline-flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm text-gray-600">Processing...</span>
              </div>
            </div>
          )}

          {message && (
            <div className={`p-4 rounded-lg border ${
              message.type === 'success' 
                ? 'bg-green-50 border-green-200 text-green-800' 
                : 'bg-red-50 border-red-200 text-red-800'
            }`}>
              <div className="flex items-center">
                {message.type === 'success' ? (
                  <svg className="h-5 w-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
                <span className="text-sm font-medium">{message.text}</span>
              </div>
            </div>
          )}

          <div className="mt-8 text-center">
            {message?.type === 'error' ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  There was an error processing your Google authentication.
                </p>
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => navigate('/login')}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
                  >
                    Back to Login
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-600">
                <a href="/login" className="font-semibold text-green-600 hover:text-green-700 transition-colors duration-200">
                  Back to Login
                </a>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleCallbackPage; 